import React, { useContext, useEffect, useState } from 'react';
import { ContentChart } from '@meteor/frontend-core';
import { AppContext } from '../../../../contexts/AppContext';
import { GLOBAL_EVENT } from '../../../../consts/common/global-event';
import APIList from '../../../../http/ApiList';
import { cloneDeep, isArray, isObject, isString } from 'lodash';
import ErrorBoundary from '../../../common/error-boundary';

const CustomizeContentChart: React.FC<any> = (props) => {
  const { authInfo, echartTheme, globalEvent, setGlobalEvent } = useContext(AppContext);
  const { property } = props;
  const [chartLoading, setChartLoading] = useState<any>(false);
  const [chartData, setChartData] = useState<any>(null);
  const [options, setOptions] = useState<any>({});
  const [titleObj, setTitleObj] = useState<any>({ title: '', subtitle: '' });
  let labelShowFlag = false;
  const [chartOptions, setChartOptions] = useState<any>(null);
  if (property?.chartOptions?.options) {
    const op = isString(property.chartOptions.options) ? JSON.parse(property.chartOptions.options) : property.chartOptions.options;
    labelShowFlag = op.series[0]?.label?.show ?? false;
  }

  const getChartData = (event: string) => {
    APIList.getCustomizeLayoutData()
      .post({ email: authInfo?.user?.attributes?.email || '', property, event })
      .then((res: any) => {
        if (!res) return;
        setChartOptions(res.options);
        setTimeout(() => {
          setChartData(res.data);
        });

        let titleObj = { title: property.title, subtitle: property.subtitle };
        if (res.titles && isArray(res.titles) && property.titleKey) {
          res.titles.forEach((t, index) => {
            if (t.name === property.titleKey) {
              titleObj = { title: t.title, subtitle: t.subtitle };
            }
          });
        }
        setTitleObj(titleObj);
        setChartLoading(false);
      });
  };

  useEffect(() => {
    if (!property || !property.chartOptions || !property.chartOptions.options) {
      return;
    }
    let tempOptions = null;
    if (chartOptions) {
      tempOptions = chartOptions;
    } else {
      tempOptions = property.chartOptions.options;
    }
    try {
      let op;
      if (tempOptions && isString(tempOptions)) {
        const reviver = (key, value) => {
          const arrowFunctionRegex = /^\s*\(?\s*([^)]*)\s*\)?\s*=>\s*{[^]*}$/;
          if (typeof value === 'string' && (value.startsWith('function') || arrowFunctionRegex.test(value))) {
            return new Function('return ' + value)();
          }
          return value;
        };
        const tempOp = JSON.parse(tempOptions, reviver);
        op = {
          ...options,
          ...tempOp,
        };
      } else if (tempOptions && isObject(tempOptions)) {
        op = {
          ...options,
          ...tempOptions,
        };
      }
      setOptions(cloneDeep({ ...op, echartTheme }));
    } catch (error) {}
  }, [property, chartOptions]);

  useEffect(() => {
    if (!globalEvent) {
      return;
    }
    if (
      (property.dataSource &&
        property.dataSource.triggerEvent &&
        property.dataSource.triggerEvent.find((triggerEvent) => {
          return triggerEvent.event === globalEvent.eventType;
        })) ||
      globalEvent.eventType === GLOBAL_EVENT.PAGE_INIT ||
      globalEvent.eventType === GLOBAL_EVENT.SHOW_CHAT_CONTENT
    ) {
      setChartLoading(true);
      setTimeout(() => {
        getChartData(globalEvent);
      }, 500);
    }
  }, [globalEvent]);

  useEffect(() => {
    if (!chartData || chartData.length === 0) {
      return;
    }
    if (isArray(chartData) && chartData[0] && chartData[0].source) {
      // dataset的source 自行配置的场合
      options.dataset = chartData;
    } else if (isArray(chartData) && isArray(options.series) && chartData[0] && chartData[0].seriesData) {
      // series data 自行配置的场合, series的data放到seriesData, 默认chartData数据和series维度一致，且都是数组
      for (let index = 0; index < options.series.length; index++) {
        options.series[index] = { ...options.series[index], ...chartData[index].seriesData };
      }
    } else {
      let formatData = chartData;
      let markArrayColumn = [];
      let markArrayData = [];
      let markAreaIndex = -1;
      let hasYAxisSet = -1;

      if (isArray(formatData[0])) {
        // 添加markArea区域
        markAreaIndex = formatData[0].findIndex((t) => {
          return t === 'markArea';
        });
        // 添加Y最大最小区域
        hasYAxisSet = formatData[0].findIndex((t) => {
          return t === 'min-max';
        });
      }

      if (markAreaIndex !== -1) {
        formatData = formatData.map((cd, i) => {
          if (i !== 0) {
            markArrayColumn.push(cd[markAreaIndex]);
          }
          cd.splice(markAreaIndex, 1);
          return cd;
        });
        markArrayColumn.forEach((m) => {
          if (m && m !== '-' && m.split(',').length === 4) {
            let tmpList = m.split(',');
            markArrayData.push([{ coord: [tmpList[0], tmpList[1]] }, { coord: [tmpList[2], tmpList[3]] }]);
          }
        });
        if (markArrayData.length > 0) {
          options.series[options.series.length - 1]['markArea'] = {
            itemStyle: {
              color: 'pink',
              opacity: 0.8,
            },
            silent: true,
            data: markArrayData,
          };
        }
      }

      if (hasYAxisSet !== -1) {
        let settings = '';
        let reg = /[\d,\d]/;
        formatData = formatData.map((fd, i) => {
          if (i !== 0 && fd[hasYAxisSet] && fd[hasYAxisSet] !== '-') {
            settings = fd[hasYAxisSet];
          }
          fd.splice(hasYAxisSet, 1);
          return fd;
        });
        if (reg.test(settings)) {
          let tmpSetting = settings.substring(1, settings.length - 1).split(',');
          options.yAxis.max = tmpSetting[1];
          options.yAxis.min = tmpSetting[0];
        }
      }

      // 默认返回纯dataset的source数据
      options.dataset = {
        source: formatData,
      };
    }

    setOptions(cloneDeep({ ...options, echartTheme }));
  }, [chartData]);

  return (
    <ErrorBoundary>
      <ContentChart
        title={{
          value: (
            <>
              <span style={{ fontSize: property.titleFontSize ? property.titleFontSize + 'px' : 'inherit' }}>{titleObj.title}</span>{' '}
              <span style={{ float: 'right' }}>{titleObj.subtitle}</span>
            </>
          ),
          styles: {},
        }}
        contentList={[]}
        chartOptions={options}
        showSeriesLabel={labelShowFlag}
        mouseWheelZoomLock={false}
        loading={chartLoading}
        height={'90%'}
        tooltips={['zoomIn']}
        isBank={!chartData || chartData.length === 0}
        onChartEvent={setGlobalEvent}
      />
    </ErrorBoundary>
  );
};

export default CustomizeContentChart;
